import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { VgpsLivekitProvider } from '@vgps/livekit_web/react';
import { ensureRuntime, disposeRuntime } from './runtime';
import { SessionEventSurface } from './SessionEventSurface';

/**
 * LivekitRuntimeProvider bootstraps the vGPS Livekit signaling runtime and
 * exposes it via VgpsLivekitProvider context.
 *
 * vgps_cms_fe stores the access token in localStorage('access-token'). We
 * react to two signals so a same-tab login → /app/tracking navigation picks
 * up the token without a hard reload:
 *
 *   1. Redux `authSlice.isLogin`: flips to true synchronously after the login
 *      reducer commits, before any route navigation. This is our primary
 *      trigger — most reliable because Redux state is the source of truth in
 *      this app.
 *   2. `storage` event: only fires for cross-tab changes (e.g. another tab
 *      logged in). Kept as a secondary trigger so multi-tab sessions stay
 *      in sync.
 *
 * Initial mount still reads localStorage directly so page reload after
 * login works.
 */
export function LivekitRuntimeProvider({ children }) {
  const isLogin = useSelector((state) => state.authSlice?.isLogin);

  const [accessToken, setAccessToken] = useState(
    () => localStorage.getItem('access-token')
  );
  const [runtime, setRuntime] = useState(null);

  // Re-read token from localStorage whenever Redux says the auth state
  // flipped. This catches the same-tab login flow that the storage event
  // misses.
  useEffect(() => {
    setAccessToken(localStorage.getItem('access-token'));
  }, [isLogin]);

  // Cross-tab fallback.
  useEffect(() => {
    const onStorage = () => setAccessToken(localStorage.getItem('access-token'));
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (!accessToken) {
      disposeRuntime().then(() => setRuntime(null));
      return undefined;
    }

    let cancelled = false;
    let retryTimer = null;
    let attempt = 0;

    // Subscriptions to runtime.onSignalingStateChange — captured so the
    // outer effect cleanup can call each unsubscribe. Previously this was
    // wrapped in an IIFE that discarded the return value, leaking a
    // listener on every token refresh.
    const stateUnsubscribers = [];

    const tryConnect = () => {
      ensureRuntime(accessToken)
        .then((rt) => {
          if (cancelled) return;
          attempt = 0;
          setRuntime(rt);
          // Subscribe to subsequent signaling state changes so this hook
          // re-publishes the runtime ref on flap (drop → reconnect). The
          // signaling_connection SdkEvent is the canonical signal; this
          // optional callback is a convenience for hosts that don't go
          // through the event bus.
          const unsubscribe = rt.onSignalingStateChange?.((s) => {
            if (cancelled) return;
            if (s === 'reconnecting') setRuntime(null);
            if (s === 'connected')    setRuntime(rt);
          });
          if (unsubscribe) stateUnsubscribers.push(unsubscribe);
        })
        .catch((err) => {
          if (cancelled) return;
          attempt += 1;
          const delay = Math.min(1000 * 2 ** attempt, 30000);
          console.warn(`[Livekit] connect failed (attempt ${attempt}), retry in ${delay}ms`, err);
          retryTimer = setTimeout(tryConnect, delay);
        });
    };

    tryConnect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      // Tear down ALL subscriptions accumulated across reconnects.
      for (const unsub of stateUnsubscribers) {
        try { unsub(); } catch { /* isolate */ }
      }
    };
  }, [accessToken]);

  return (
    <VgpsLivekitProvider runtime={runtime} disconnectSignalingWhenHidden>
      <SessionEventSurface />
      {children}
    </VgpsLivekitProvider>
  );
}
